import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  LeftOutlined,
  PlusOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { SUBANTA_LINGA, SUBANTA_PRAKAR, enumToOptions } from '../constants/subantaEnums';
import type { SubantaLinga, SubantaPrakar } from '../api/subantaApi';
import {
  buildSubantaRupFormulaPatch,
  createBodyFromFormValues,
  createSubantaRupFormula,
  deleteSubantaRupFormula,
  formulaToFormValues,
  getSubantaRupFormula,
  listSubantaRupFormulas,
  updateSubantaRupFormula,
  type SubantaRupFormula,
  type SubantaRupFormulaFormValues,
} from '../api/subantaRupFormulaApi';

const prakarOptions = enumToOptions(SUBANTA_PRAKAR);
const lingaOptions = enumToOptions(SUBANTA_LINGA);

const triStateBoolFilterOptions = [
  { value: 'all', label: 'All' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
] as const;

const defaultCreateValues: Partial<SubantaRupFormulaFormValues> = {
  prakar: SUBANTA_PRAKAR[0],
  linga: SUBANTA_LINGA[0],
  natwa: false,
  shatwa: false,
  roop_prakar: undefined,
  aadhar_shabda: undefined,
  vishesh: undefined,
  bat: undefined,
};

export function SubantaRupFormulaPage() {
  const [rows, setRows] = useState<SubantaRupFormula[]>([]);
  const [filterPrakar, setFilterPrakar] = useState<SubantaPrakar | undefined>(undefined);
  const [filterLinga, setFilterLinga] = useState<SubantaLinga | undefined>(undefined);
  const [filterNatwa, setFilterNatwa] = useState<'all' | 'yes' | 'no'>('all');
  const [filterShatwa, setFilterShatwa] = useState<'all' | 'yes' | 'no'>('all');
  const [filterBatInput, setFilterBatInput] = useState('');
  const [listPage, setListPage] = useState(1);
  const [listPageSize, setListPageSize] = useState(15);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SubantaRupFormula | null>(null);
  const [saving, setSaving] = useState(false);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [form] = Form.useForm<SubantaRupFormulaFormValues>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listSubantaRupFormulas());
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(
        ax?.response?.data?.message || ax?.message || 'Failed to load subanta rup formulas'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredRows = useMemo(() => {
    const batQ = filterBatInput.trim().toLowerCase();
    return rows.filter((r) => {
      if (filterPrakar != null && r.prakar !== filterPrakar) return false;
      if (filterLinga != null && r.linga !== filterLinga) return false;
      if (filterNatwa === 'yes' && !(r.natwa ?? false)) return false;
      if (filterNatwa === 'no' && (r.natwa ?? false)) return false;
      if (filterShatwa === 'yes' && !(r.shatwa ?? false)) return false;
      if (filterShatwa === 'no' && (r.shatwa ?? false)) return false;
      if (batQ) {
        const b = r.bat != null ? String(r.bat).trim().toLowerCase() : '';
        if (!b.includes(batQ)) return false;
      }
      return true;
    });
  }, [rows, filterPrakar, filterLinga, filterNatwa, filterShatwa, filterBatInput]);

  useEffect(() => {
    setListPage(1);
  }, [filterPrakar, filterLinga, filterNatwa, filterShatwa, filterBatInput]);

  const listTotalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredRows.length / listPageSize)),
    [filteredRows.length, listPageSize]
  );

  useEffect(() => {
    setListPage((p) => Math.min(p, listTotalPages));
  }, [listTotalPages]);

  const handleFormulaTablePagination = useCallback((page: number, pageSize: number) => {
    setListPage(page);
    setListPageSize(pageSize);
  }, []);

  const clearListFilters = useCallback(() => {
    setFilterPrakar(undefined);
    setFilterLinga(undefined);
    setFilterNatwa('all');
    setFilterShatwa('all');
    setFilterBatInput('');
    setListPage(1);
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue(defaultCreateValues as SubantaRupFormulaFormValues);
    setModalOpen(true);
  };

  const openEdit = useCallback(
    async (record: SubantaRupFormula) => {
      setEditing(record);
      form.resetFields();
      setModalOpen(true);
      setPrefillLoading(true);
      try {
        const fresh = await getSubantaRupFormula(record.id);
        setEditing(fresh);
        form.setFieldsValue(formulaToFormValues(fresh));
      } catch (err: unknown) {
        const ax = err as { response?: { data?: { message?: string } }; message?: string };
        message.error(ax?.response?.data?.message || ax?.message || 'Failed to load formula');
        setModalOpen(false);
        setEditing(null);
      } finally {
        setPrefillLoading(false);
      }
    },
    [form]
  );

  const openCopyFrom = useCallback(
    async (record: SubantaRupFormula) => {
      setEditing(null);
      form.resetFields();
      setModalOpen(true);
      setPrefillLoading(true);
      try {
        const fresh = await getSubantaRupFormula(record.id);
        form.setFieldsValue(formulaToFormValues(fresh));
        message.success('Formula copied — save as a new row when ready');
      } catch (err: unknown) {
        const ax = err as { response?: { data?: { message?: string } }; message?: string };
        message.error(ax?.response?.data?.message || ax?.message || 'Failed to load formula');
        setModalOpen(false);
      } finally {
        setPrefillLoading(false);
      }
    },
    [form]
  );

  /** Same order as the filtered table; prev/next only apply in edit mode. */
  const formulaNav = useMemo(() => {
    const id = editing?.id;
    if (id == null) {
      return { prevId: null as number | null, nextId: null as number | null, index: -1, total: 0 };
    }
    const ids = filteredRows.map((r) => r.id);
    const idx = ids.indexOf(id);
    if (idx < 0) {
      return { prevId: null, nextId: null, index: -1, total: ids.length };
    }
    return {
      prevId: idx > 0 ? ids[idx - 1]! : null,
      nextId: idx < ids.length - 1 ? ids[idx + 1]! : null,
      index: idx,
      total: ids.length,
    };
  }, [editing?.id, filteredRows]);

  const goToAdjacentFormula = useCallback(
    async (targetId: number | null) => {
      if (targetId == null) return;
      const record = filteredRows.find((r) => r.id === targetId);
      if (!record) {
        message.warning('Formula not found in the current filtered list.');
        return;
      }
      await openEdit(record);
    },
    [filteredRows, openEdit]
  );

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing) {
        const patch = buildSubantaRupFormulaPatch(editing, values);
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeModal();
          return;
        }
        await updateSubantaRupFormula(editing.id, patch);
        message.success('Formula updated');
      } else {
        await createSubantaRupFormula(createBodyFromFormValues(values));
        message.success('Formula created');
      }
      closeModal();
      await load();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteSubantaRupFormula(id);
      message.success('Formula deleted');
      await load();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Delete failed');
    }
  };

  const formulaFormInner = (
    <>
      <Space wrap style={{ width: '100%' }}>
        <Form.Item
          name="prakar"
          label="Prakar"
          rules={[{ required: true, message: 'Select prakar' }]}
          style={{ minWidth: 220, flex: 1 }}
        >
          <Select showSearch optionFilterProp="label" options={prakarOptions} />
        </Form.Item>
        <Form.Item
          name="linga"
          label="Linga"
          rules={[{ required: true, message: 'Select linga' }]}
          style={{ minWidth: 220, flex: 1 }}
        >
          <Select showSearch optionFilterProp="label" options={lingaOptions} />
        </Form.Item>
      </Space>

      <Space wrap style={{ width: '100%' }}>
        <Form.Item name="roop_prakar" label="Roop prakar" style={{ minWidth: 200, flex: 1 }}>
          <Input placeholder="Optional" />
        </Form.Item>
        <Form.Item name="aadhar_shabda" label="Aadhar shabda" style={{ minWidth: 200, flex: 1 }}>
          <Input placeholder="Optional" />
        </Form.Item>
      </Space>

      <Form.Item name="vishesh" label="Vishesh">
        <Input placeholder="Optional" />
      </Form.Item>

      <Form.Item name="bat" label="Bat">
        <Input placeholder="Optional (empty stored as null)" />
      </Form.Item>

      <Space size="large" style={{ marginBottom: 16 }}>
        <Form.Item name="natwa" label="Natwa" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="shatwa" label="Shatwa" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Space>

      <div style={{ marginTop: 8, overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 13,
          }}
        >
          <thead>
            <tr>
              <th style={{ width: 48, border: '1px solid #f0f0f0', padding: 4 }} />
              {[1, 2, 3].map((c) => (
                <th
                  key={c}
                  style={{
                    border: '1px solid #f0f0f0',
                    padding: 6,
                    background: '#fafafa',
                  }}
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 8 }, (_, i) => {
              const r = i + 1;
              return (
                <tr key={r}>
                  <td
                    style={{
                      border: '1px solid #f0f0f0',
                      padding: 4,
                      textAlign: 'center',
                      background: '#fafafa',
                      fontWeight: 500,
                    }}
                  >
                    {r}
                  </td>
                  {[1, 2, 3].map((c) => {
                    const name = `v_${r}_${c}` as const;
                    return (
                      <td key={c} style={{ border: '1px solid #f0f0f0', padding: 4 }}>
                        <Form.Item name={name} noStyle>
                          <Input placeholder={name} size="small" />
                        </Form.Item>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );

  const columns: ColumnsType<SubantaRupFormula> = [
      { title: 'ID', dataIndex: 'id', width: 72, fixed: 'left' },
      { title: 'Prakar', dataIndex: 'prakar', width: 140, ellipsis: true },
      { title: 'Linga', dataIndex: 'linga', width: 160, ellipsis: true },
      { title: 'Roop prakar', dataIndex: 'roop_prakar', width: 120, ellipsis: true },
      { title: 'Aadhar shabda', dataIndex: 'aadhar_shabda', width: 120, ellipsis: true },
      {
        title: 'Vishesh',
        dataIndex: 'vishesh',
        ellipsis: true,
        render: (v: string | null | undefined) =>
          v == null || v === '' ? '—' : v.length > 40 ? `${v.slice(0, 40)}…` : v,
      },
      {
        title: 'Bat',
        dataIndex: 'bat',
        width: 80,
        render: (v: string | null | undefined) => (v == null || v === '' ? '—' : v),
      },
      {
        title: 'Natwa',
        dataIndex: 'natwa',
        width: 72,
        render: (v: boolean) => (v ? 'Yes' : 'No'),
      },
      {
        title: 'Shatwa',
        dataIndex: 'shatwa',
        width: 72,
        render: (v: boolean) => (v ? 'Yes' : 'No'),
      },
      {
        title: 'Actions',
        key: 'actions',
        fixed: 'right',
        width: 200,
        render: (_, record) => (
          <Space size="small" wrap>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => void openEdit(record)}
            >
              Edit
            </Button>
            <Button
              type="link"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => void openCopyFrom(record)}
            >
              Copy
            </Button>
            <Popconfirm title="Delete this formula?" onConfirm={() => void handleDelete(record.id)}>
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Subanta rup formula
          </Typography.Title>
        </Space>
      </Card>
      <Card
        title="All formulas"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New formula
          </Button>
        }
      >
        <Space wrap style={{ marginBottom: 12 }} align="center">
          <Typography.Text type="secondary">Filter:</Typography.Text>
          <Select<SubantaPrakar>
            allowClear
            placeholder="Prakar"
            style={{ width: 200 }}
            value={filterPrakar}
            options={prakarOptions}
            showSearch
            optionFilterProp="label"
            onChange={(v) => setFilterPrakar(v)}
          />
          <Select<SubantaLinga>
            allowClear
            placeholder="Liṅga"
            style={{ width: 200 }}
            value={filterLinga}
            options={lingaOptions}
            showSearch
            optionFilterProp="label"
            onChange={(v) => setFilterLinga(v)}
          />
          <Space size={4} align="center">
            <Typography.Text type="secondary">Natwa</Typography.Text>
            <Select
              style={{ width: 100 }}
              value={filterNatwa}
              options={[...triStateBoolFilterOptions]}
              onChange={(v) => setFilterNatwa(v as 'all' | 'yes' | 'no')}
            />
          </Space>
          <Space size={4} align="center">
            <Typography.Text type="secondary">Śatva</Typography.Text>
            <Select
              style={{ width: 100 }}
              value={filterShatwa}
              options={[...triStateBoolFilterOptions]}
              onChange={(v) => setFilterShatwa(v as 'all' | 'yes' | 'no')}
            />
          </Space>
          <Input
            allowClear
            placeholder="Bat (contains)"
            style={{ width: 180 }}
            value={filterBatInput}
            onChange={(e) => setFilterBatInput(e.target.value)}
          />
          <Button onClick={clearListFilters}>Clear filters</Button>
        </Space>
        <Table<SubantaRupFormula>
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={filteredRows}
          scroll={{ x: 1100 }}
          pagination={{
            current: listPage,
            pageSize: listPageSize,
            total: filteredRows.length,
            showSizeChanger: true,
            pageSizeOptions: [10, 15, 20, 50, 100],
            showTotal: (total, range) =>
              total ? `${range[0]}-${range[1]} of ${total}` : '0',
            onChange: handleFormulaTablePagination,
          }}
        />
      </Card>

      <Modal
        title={editing ? `Edit formula #${editing.id}` : 'New subanta rup formula'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => void onSubmit()}
        confirmLoading={saving}
        okText={editing ? 'Save' : 'Create'}
        width={960}
        destroyOnClose
        footer={(_originNode, { OkBtn, CancelBtn }) => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            <CancelBtn />
            {editing != null && formulaNav.total > 0 && formulaNav.index >= 0 ? (
              <Typography.Text type="secondary">
                {formulaNav.index + 1} of {formulaNav.total}
              </Typography.Text>
            ) : (
              <span />
            )}
            <OkBtn />
          </div>
        )}
        styles={{
          body:
            editing != null
              ? {
                  maxHeight: '75vh',
                  overflow: 'hidden',
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }
              : { maxHeight: '75vh', overflowY: 'auto' },
        }}
      >
        {editing != null ? (
          <div
            style={{
              display: 'flex',
              flex: 1,
              minHeight: 0,
              flexDirection: 'row',
              alignItems: 'stretch',
              gap: 4,
              width: '100%',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                width: 44,
                paddingLeft: 6,
              }}
            >
              <Button
                type="default"
                shape="circle"
                icon={<LeftOutlined />}
                aria-label="Previous formula"
                disabled={prefillLoading || saving || formulaNav.prevId == null}
                onClick={() => void goToAdjacentFormula(formulaNav.prevId)}
                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}
              />
            </div>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                overflowY: 'auto',
                padding: '12px 8px 12px 0',
              }}
            >
              <Spin spinning={prefillLoading}>
                <Form<SubantaRupFormulaFormValues>
                  form={form}
                  layout="vertical"
                  requiredMark="optional"
                  style={{ marginTop: 8 }}
                >
                  {formulaFormInner}
                </Form>
              </Spin>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                width: 44,
                paddingRight: 6,
              }}
            >
              <Button
                type="default"
                shape="circle"
                icon={<RightOutlined />}
                aria-label="Next formula"
                disabled={prefillLoading || saving || formulaNav.nextId == null}
                onClick={() => void goToAdjacentFormula(formulaNav.nextId)}
                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}
              />
            </div>
          </div>
        ) : (
          <Spin spinning={prefillLoading}>
            <Form<SubantaRupFormulaFormValues>
              form={form}
              layout="vertical"
              requiredMark="optional"
              style={{ marginTop: 8 }}
            >
              {formulaFormInner}
            </Form>
          </Spin>
        )}
      </Modal>
    </Space>
  );
}
